import { useEffect } from "react"
import { useTranslation } from "react-i18next"
import { isFinite } from "lodash"

import { IListItem } from "components/List/List"
import NoValue from "components/NoValue/NoValue"
import { ArticleClassField, getArticleClassName } from "models/ArticleClassInfo"
import { ArticleEquipment } from "models/ArticleEquipment"
import { useUnits } from "stores/units"
import { ArticleAny, ISummarySection } from "@/types"
import { formatNumber } from "utils/utils"
import useTechnicalData from "hooks/useTechnicalData"
import { ArticleType } from "config/constants"

interface IUseArticleTechnicalDataParams {
    article: ArticleAny
}

export default function useArticleTechnicalData({
    article,
}: IUseArticleTechnicalDataParams): {
    technicalDataItems: IListItem[]
    technicalDataSubSections: ISummarySection<IListItem>[]
} {
    const { t } = useTranslation()
    const { getUnit } = useUnits()

    const {
        fetchArtcleClasses,
        articleClassHasField,
        QuickCouplingSystemTypes,
        ConnectionTypes,
        LockingTypes,
        ControlSystemTypes,
        AgilityTypes,
    } = useTechnicalData()

    useEffect(() => {
        fetchArtcleClasses()
    }, [fetchArtcleClasses])

    if (!article || !(article instanceof ArticleEquipment)) {
        return {
            technicalDataItems: [],
            technicalDataSubSections: [],
        }
    }

    const {
        articleType,
        articleClass,
        measurementSystemId,
        length,
        width,
        height,
        volume,
        weight,
        wheelWidth,
        wheelHeight,
        inch,
        loadSpeedIndex,
        constructionCode,
        isTubeless,
        starMarking,
        quickCouplingSystem,
        connectionType,
        cuttingEdge,
        teeth,
        heavyDuty,
        locking,
        controlSystem,
        agility,
    } = article

    const unitLabel = getUnit(measurementSystemId)?.printShort() || ""

    const technicalDataItems: IListItem[] = [
        {
            label: t('article.articleType'),
            value: articleType === ArticleType.zts ? t('article.liebherrZts') : t('article.notLiebherrZts')
        },
        {
            label: t('article.articleClass'),
            value: articleClass ? getArticleClassName(articleClass) : <NoValue />
        }
    ]

    if (articleClassHasField(article.articleClass, ArticleClassField.dimensions)) {
        const dimensions = [
            typeof length === "number" ? `${t('common.dimensionShort.length')} ${formatNumber(length)} ${unitLabel}` : null,
            typeof width === "number" ? `${t('common.dimensionShort.width')} ${formatNumber(width)} ${unitLabel}` : null,
            typeof height === "number" ? `${t('common.dimensionShort.height')} ${formatNumber(height)} ${unitLabel}` : null,
        ].filter(d => !!d)

        technicalDataItems.push({
            label: t('common.dimensions'),
            value: dimensions.length > 0 ? dimensions.join(" x ") : <NoValue />
        })
    }

    if (articleClassHasField(article.articleClass, ArticleClassField.volume)) {
        technicalDataItems.push({
            label: t('common.volume'),
            value: typeof volume === "number" ? formatNumber(volume) + " " + t('common.m3') : <NoValue />
        })
    }

    if (articleClassHasField(article.articleClass, ArticleClassField.weight)) {
        technicalDataItems.push({
            label: t('common.weight'),
            value: typeof weight === "number" ? formatNumber(weight) + " " + t('common.kg') : <NoValue />
        })
    }

    const technicalDataSubSections: ISummarySection<IListItem>[] = []
    const articleClassSpecificAttributesItems: IListItem[] = []

    if (articleClassHasField(articleClass, ArticleClassField.wheel)) {
        const wheelDimensions = [wheelWidth, wheelHeight]
            .filter(d => typeof d === "number")
            .map(d => formatNumber(d!))

        let radiusLabel = ""
        if (typeof inch === "number") {
            radiusLabel = `R ${formatNumber(inch)}`
        }

        articleClassSpecificAttributesItems.push({
            label: t('common.dimensions'),
            value: wheelDimensions.length > 0 || radiusLabel ? [wheelDimensions.join(" / "), radiusLabel].join(" ") : <NoValue />
        })

        articleClassSpecificAttributesItems.push({
            label: t('article.loadSpeedIndex'),
            value: loadSpeedIndex || <NoValue />
        })

        articleClassSpecificAttributesItems.push({
            label: t('article.constructionCode'),
            value: constructionCode || <NoValue />
        })

        articleClassSpecificAttributesItems.push({
            label: t('article.tubeless'),
            value: typeof isTubeless === 'boolean' ? (isTubeless ? t('common.yes') : t('common.no')) : <NoValue />
        })

        articleClassSpecificAttributesItems.push({
            label: t('article.starMarking'),
            value: isFinite(starMarking) ? Array(starMarking! + 1).join("*") : <NoValue />
        })
    }

    if (articleClassHasField(article.articleClass, ArticleClassField.quickCouplingSystem)) {
        articleClassSpecificAttributesItems.push({
            label: t('asset.qcQuickCoupler'),
            value: quickCouplingSystem ? (QuickCouplingSystemTypes.get(quickCouplingSystem) || quickCouplingSystem || <NoValue />) : <NoValue />
        })
    }

    if (articleClassHasField(article.articleClass, ArticleClassField.connectionType)) {
        articleClassSpecificAttributesItems.push({
            label: t('asset.connectionType'),
            value: connectionType ? (ConnectionTypes.get(connectionType) || connectionType || <NoValue />) : <NoValue />
        })
    }

    if (articleClassHasField(article.articleClass, ArticleClassField.cuttingEdge)) {
        articleClassSpecificAttributesItems.push({
            label: t('asset.bocBoltOnCuttingEdge'),
            value: cuttingEdge ? t('common.yes') : (cuttingEdge === false ? t('common.no') : <NoValue />)
        })
    }

    if (articleClassHasField(article.articleClass, ArticleClassField.teeth)) {
        articleClassSpecificAttributesItems.push({
            label: t('asset.teeth'),
            value: teeth ? t('common.yes') : (teeth === false ? t('common.no') : <NoValue />)
        })
    }

    if (articleClassHasField(article.articleClass, ArticleClassField.heavyDuty)) {
        articleClassSpecificAttributesItems.push({
            label: t('asset.hdHeavyDuty'),
            value: heavyDuty ? t('common.yes') : (heavyDuty === false ? t('common.no') : <NoValue />)
        })
    }

    if (articleClassHasField(article.articleClass, ArticleClassField.locking)) {
        articleClassSpecificAttributesItems.push({
            label: t('asset.locking'),
            value: locking ? (LockingTypes.get(locking) || locking || <NoValue />) : <NoValue />
        })
    }

    if (articleClassHasField(article.articleClass, ArticleClassField.controlSystem)) {
        articleClassSpecificAttributesItems.push({
            label: t('asset.controlSystem'),
            value: controlSystem ? (ControlSystemTypes.get(controlSystem) || controlSystem || <NoValue />) : <NoValue />
        })
    }

    if (articleClassHasField(article.articleClass, ArticleClassField.agility)) {
        articleClassSpecificAttributesItems.push({
            label: t('asset.agility'),
            value: agility ? (AgilityTypes.get(agility) || agility || <NoValue />) : <NoValue />
        })
    }

    if (articleClassSpecificAttributesItems.length > 0) {
        technicalDataSubSections.push({
            title: t('article.articleClassSpecificAttributes'),
            items: articleClassSpecificAttributesItems,
        })
    }

    return {
        technicalDataItems,
        technicalDataSubSections,
    }
}
